import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const emptyPromo = {
  name: "",
  type: "percent",
  value: "",
  code: "",
  scope: "all",
  starts_at: "",
  ends_at: "",
  active: true,
};

const TYPE_LABELS = { percent: "Pourcentage", fixed: "Montant fixe" };
const SCOPE_LABELS = {
  all: "Toutes les activités",
  emporter: "À emporter",
  comptoir: "Le Comptoir",
  services: "Maasaï Services",
};

export default function PromotionsManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/promotions").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...editing,
        value: parseFloat(String(editing.value).replace(",", ".")) || 0,
        code: (editing.code || "").trim().toUpperCase(),
      };
      if (payload.value <= 0) {
        toast.error("La valeur de la promotion doit être supérieure à 0.");
        setSaving(false);
        return;
      }
      if (editing.id) {
        await api.put(`/admin/promotions/${editing.id}`, payload);
      } else {
        await api.post("/admin/promotions", payload);
      }
      toast.success("Promotion enregistrée.");
      setEditing(null);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/promotions/${id}`);
    toast.success("Promotion supprimée.");
    load();
  };

  const set = (key) => (e) => setEditing((s) => ({ ...s, [key]: e.target.value }));

  return (
    <div data-testid="promotions-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ivory">Promotions — {items.length}</h2>
          <p className="mt-1 text-xs text-ivory/40">
            Non cumulables : la meilleure offre s'applique automatiquement. Sans code, la promotion
            est affichée (prix barré) et appliquée seule ; avec code, le client le saisit au
            paiement.
          </p>
        </div>
        <button onClick={() => setEditing({ ...emptyPromo })} className="btn-primary !h-10 !px-5 !text-[11px]" data-testid="promo-add-button">
          <Plus className="h-4 w-4" strokeWidth={1.5} />
          Ajouter une promotion
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="promo-edit-modal">
          <form onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto rounded-lg border border-white/10 bg-coal p-8">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-ivory">
                {editing.id ? "Modifier la promotion" : "Nouvelle promotion"}
              </h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer">
                <X className="h-5 w-5 text-ivory/50 hover:text-ivory" strokeWidth={1.5} />
              </button>
            </div>
            <input required value={editing.name} onChange={set("name")} placeholder="Nom (ex : Offre de lancement)" className="input-dark !h-11" data-testid="promo-name-input" />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Type</label>
                <Select value={editing.type} onValueChange={(v) => setEditing((s) => ({ ...s, type: v }))}>
                  <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="promo-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-coal text-ivory">
                    {Object.entries(TYPE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Valeur {editing.type === "percent" ? "(%)" : "(€)"}
                </label>
                <input required value={editing.value} onChange={set("value")} placeholder={editing.type === "percent" ? "10" : "5"} className="input-dark !h-11" data-testid="promo-value-input" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Portée</label>
                <Select value={editing.scope} onValueChange={(v) => setEditing((s) => ({ ...s, scope: v }))}>
                  <SelectTrigger className="!h-11 border-white/10 bg-white/5 text-sm text-ivory" data-testid="promo-scope-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-coal text-ivory">
                    {Object.entries(SCOPE_LABELS).map(([v, l]) => (
                      <SelectItem key={v} value={v}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">
                  Code promo (vide = automatique)
                </label>
                <input value={editing.code} onChange={set("code")} placeholder="EX : LANCEMENT10" className="input-dark !h-11 uppercase" data-testid="promo-code-input" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Début (optionnel)</label>
                <input type="date" value={editing.starts_at} onChange={set("starts_at")} className="input-dark !h-11 [color-scheme:dark]" data-testid="promo-starts-input" />
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-ivory/50">Fin (optionnel)</label>
                <input type="date" value={editing.ends_at} onChange={set("ends_at")} className="input-dark !h-11 [color-scheme:dark]" data-testid="promo-ends-input" />
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm text-ivory/70">
              Active
              <Switch checked={editing.active} onCheckedChange={(v) => setEditing((s) => ({ ...s, active: v }))} data-testid="promo-active-switch" />
            </label>
            <button type="submit" disabled={saving} className="btn-primary w-full" data-testid="promo-save-button">
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-ivory/40" data-testid="promotions-empty">
          Aucune promotion pour le moment. Créez votre première offre (lancement, saisonnière, code
          promo…).
        </p>
      ) : (
        <div className="space-y-2" data-testid="promotions-list">
          {items.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-white/10 bg-coal/60 px-5 py-3.5" data-testid={`promo-row-${p.id}`}>
              <span className={`text-sm font-semibold ${p.active ? "text-ivory" : "text-ivory/35 line-through"}`}>
                {p.name}
              </span>
              <span className="rounded border border-gold/30 px-2 py-0.5 text-[11px] font-semibold text-gold">
                {p.type === "percent" ? `-${p.value} %` : `-${String(p.value).replace(".", ",")} €`}
              </span>
              <span className="text-xs text-ivory/50">{SCOPE_LABELS[p.scope] || p.scope}</span>
              {p.code && (
                <span className="rounded border border-white/15 px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider text-ivory/60">
                  {p.code}
                </span>
              )}
              {(p.starts_at || p.ends_at) && (
                <span className="text-xs text-ivory/40">
                  {p.starts_at || "…"} → {p.ends_at || "…"}
                </span>
              )}
              {!p.active && <span className="text-xs text-ivory/35">inactive</span>}
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setEditing({ ...p })} aria-label="Modifier" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/50 transition-colors hover:border-gold hover:text-gold" data-testid={`promo-edit-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
                <button onClick={() => remove(p.id)} aria-label="Supprimer" className="flex h-8 w-8 items-center justify-center rounded-md border border-white/10 text-ivory/40 transition-colors hover:border-red-400/50 hover:text-red-400" data-testid={`promo-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
